/**
 * Extract client names from invoice trip lines.
 * Handles multiple description formats produced over time:
 *   - "Trip bookings first|final payment: NAME, NAME, ..."
 *   - "First Payment 40% (NAME, NAME, ... y otros)"
 *   - "Final Payment + Shared Profit) — Trip bookings final payment: NAME, ..."
 */
export function extractClientNames(lines) {
  const names = new Set();
  for (const ln of lines || []) {
    if (ln.group !== "trips" || ln.is_header) continue;
    const desc = String(ln.description || "");

    // Pattern 1: explicit "Trip bookings X payment: <names>"
    const m1 = desc.match(/Trip bookings (?:first|final) payment\s*:\s*(.+)/i);
    if (m1) {
      m1[1].split(",").forEach((n) => addName(names, n));
      continue;
    }

    // Pattern 2: "(name, name, name y otros)" — legacy seed format
    const m2 = desc.match(/(?:First Payment|Final Payment)[^\(]*\(([^)]+)\)/i);
    if (m2) {
      m2[1].split(",").forEach((n) => addName(names, n));
    }
  }
  return [...names];
}

function addName(set, raw) {
  let s = String(raw || "").trim();
  // Strip trailing "y otros", "etc.", etc.
  s = s.replace(/\s+y\s+otros\.?$/i, "").replace(/\s+etc\.?$/i, "").trim();
  if (!s) return;
  // Strip parenthetical sub-info like "(En KK Grace Green, su hija)"
  s = s.replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim();
  if (s.length < 2) return;
  set.add(normalizeName(s));
}

export function normalizeName(name) {
  return String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * For a Final invoice about to be emitted, find trips whose Adelanto is 0
 * but the same client appears in a previously-emitted Advance invoice.
 *
 * @param {Array} currentTrips trips state from the Final form (with adelanto_eur)
 * @param {Array} allInvoices full invoice list from backend
 * @returns Array of { name, advanceInvoices: string[] }
 */
export function findTripsMissingAdelanto(currentTrips, allInvoices) {
  const advances = (allInvoices || []).filter(
    (i) => i.type === "advance" && i.status === "issued"
  );

  // Build a map: normalized name -> array of invoice numbers
  const map = new Map();
  for (const inv of advances) {
    const fromLines = extractClientNames(inv.lines);
    const fromTrips = (inv.trips || [])
      .map((t) => normalizeName(t.client_name))
      .filter(Boolean);
    const all = new Set([...fromLines, ...fromTrips]);
    for (const name of all) {
      if (!map.has(name)) map.set(name, []);
      if (!map.get(name).includes(inv.number)) {
        map.get(name).push(inv.number);
      }
    }
  }

  const warnings = [];
  for (const trip of currentTrips || []) {
    if (Number(trip.adelanto_eur || 0) > 0) continue;
    if (!trip.client_name) continue;
    const norm = normalizeName(trip.client_name);
    if (map.has(norm)) {
      warnings.push({
        name: trip.client_name,
        advanceInvoices: map.get(norm),
      });
    }
  }
  return warnings;
}

/**
 * Find duplicate client names already used in previous emitted invoices of the
 * same type.
 * @param {Array} currentLines lines about to be emitted
 * @param {Array} allInvoices full invoice list from backend
 * @param {string} type 'advance' | 'final'
 * @param {string|null} currentId id of the invoice being edited (excluded from comparison)
 * @returns Array of { name, invoiceNumber } objects
 */
export function findDuplicateNames(currentLines, allInvoices, type, currentId = null) {
  const currentNames = extractClientNames(currentLines);
  if (currentNames.length === 0) return [];

  const duplicates = [];
  for (const inv of allInvoices) {
    if (inv.type !== type) continue;
    if (inv.status !== "issued") continue;
    if (currentId && inv.id === currentId) continue;
    const prevNames = new Set(extractClientNames(inv.lines));
    for (const name of currentNames) {
      if (prevNames.has(name)) {
        // Try to recover the original-cased name from the current lines
        const original = recoverOriginalName(currentLines, name);
        duplicates.push({ name: original, invoiceNumber: inv.number });
      }
    }
  }
  return duplicates;
}

function recoverOriginalName(lines, normalized) {
  for (const ln of lines || []) {
    if (ln.group !== "trips" || ln.is_header) continue;
    const m = String(ln.description || "").match(/Trip bookings (?:first|final) payment\s*:\s*(.+)/i);
    if (!m) continue;
    const list = m[1].split(",").map((s) => s.trim());
    const found = list.find((n) => normalizeName(n) === normalized);
    if (found) return found;
  }
  return normalized;
}
